import React from 'react';
import _ from 'lodash';
import styles from './MockTemplateDetail.module.css';
import Button from 'react-bootstrap/Button';
import { MockParameterState } from 'views/MockServices/Tabs/MockServices/MockParameters';
import SimpleLoader from 'components/loaders/SimpleLoader';
import { WithTranslation, withTranslation } from 'react-i18next';
import { updateMockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplatesActions';
import { connect } from 'react-redux';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ApplicationState } from 'store/rootReducer';
import {
  discardLocalTemplateChanges,
  getAssetsForTemplate,
  updateActiveTab,
  updateTemplateActiveParamTab,
  updateTemplateAssets,
} from 'store/mockServiceTemplates/mockServiceTemplateDetail/mockServiceTemplateDetailActions';
import { Transaction } from 'types/rest/assetCatalogTypes';
import { dateAndAuthorFormat } from 'utils/dateUtil';
import { getPublishConfig } from 'utils/blazeDataUtils';
import { IterationOverrides } from 'types/rest/blazeDataTypes';
import ConfigurationsDropDown from '../../MockServicesFilter/ConfigurationsDropDown';
import HelpTooltip from 'components/overlays/helpTooltip/HelpTooltip';
import MockServiceDetailTabs from '../MockServices/MockServiceDetail/MockServiceDetailTabs';
import { MockService } from 'types/rest/mockServiceTypes';
import { Col, Row } from 'react-bootstrap';

interface MockTemplateDetailProps {
  template: MockServiceTemplate;
  updateTemplateHandler: (template: MockServiceTemplate) => void;
  updateTransactionHandler: (transaction: Transaction, afterSaveCallback?: any) => void;
  saveTemplateHandler: () => void;
  isSaving: boolean;
  invalid: boolean;
  isContainerScalable: boolean;
  isReadOnly?: boolean;
}

type AllProps = StateProps & MockTemplateDetailProps & WithTranslation & DispatchProps;

class MockTemplateDetail extends React.Component<AllProps> {
  state = {
    refreshTimestamp: 0,
  };

  _isMounted = false;

  componentDidMount() {
    this._isMounted = true;

    //Load assets only if they are empty...
    if (this.props.assetBundle.assets === null) {
      this.props.getAssetsForTemplate(this.props.template);
    }
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  onUpdateTemplateProps = (paramsState: MockParameterState) => {
    const updatedTemplate = Object.assign({}, this.props.template, paramsState);
    this.props.updateTemplateHandler(updatedTemplate);
  };

  onUpdateTemplatePropsDebounced = _.debounce((paramsState: MockParameterState) => {
    const updatedTemplate = Object.assign({}, this.props.template, paramsState);
    this.props.updateTemplateHandler(updatedTemplate);
  }, 600);

  updateDescriptionHandler = _.debounce((description: string) => {
    const updatedTemplate = Object.assign({}, this.props.template);
    updatedTemplate.description = description;
    this.props.updateTemplateHandler(updatedTemplate);
  }, 600);

  updateConfigurationHandler = _.debounce((id: number | null) => {
    const updatedTemplate = Object.assign({}, this.props.template);
    updatedTemplate.configurationId = String(id) !== String(-1) ? id : null;
    this.props.updateTemplateHandler(updatedTemplate);
  }, 600);

  updateDataPublishConfigHandler = (iterationOverrides: IterationOverrides) => {
    const mockServiceTemplate = _.cloneDeep(this.props.template);
    mockServiceTemplate.dataSettings = getPublishConfig(iterationOverrides);
    this.props.updateMockServiceTemplate(mockServiceTemplate);
  };

  render() {
    const { t } = this.props;
    const createdMessage = dateAndAuthorFormat(this.props.template.created, this.props.template.createdBy);
    const updatedMessage = dateAndAuthorFormat(this.props.template.updated, this.props.template.updatedBy);
    return (
      <div>
        <Row>
          {/* Description */}
          <Col md={4}>
            <label htmlFor={this.props.template.id + '-template-desc'} className="d-block">
              {t('mockServiceDetails.description')}
            </label>
            <input
              id={this.props.template.id + '-template-desc'}
              readOnly={this.props.isReadOnly}
              type="text"
              className="form-control"
              defaultValue={this.props.template.description}
              onChange={(e: any) => this.updateDescriptionHandler(e.target.value)}
            />
          </Col>
          {/* ./Description */}

          {/* Configuration */}
          <Col md={2}>
            <div style={{ position: 'relative', marginTop: '-6px', marginBottom: '-5px' }}>
              <span className={'form-label'} style={{ position: 'relative', bottom: '3px', top: '-5px' }}>
                <label htmlFor={'configuration-picker' + this.props.template.configurationId}>
                  {t('mockServiceDetails.configuration')}
                </label>
              </span>
              <span id="serviceHelp" className={styles.questionInTableHeader} title={this.props.t('common:showHelp')}>
                <HelpTooltip
                  id={'mock-services-intro-help'}
                  style={{ position: 'relative', right: '-6px' }}
                  fontSize={22}
                />
              </span>
            </div>
            <ConfigurationsDropDown
              id={this.props.template.configurationId}
              configurationsList={this.props.configurations}
              selectedConfigurationId={this.props.template.configurationId}
              onChangeConfiguration={this.updateConfigurationHandler}
              disabled={this.props.isReadOnly}
            />
          </Col>
          {/* ./Configuration */}

          {/* Created */}
          <Col md={2}>
            <label className={styles.mockServiceDetailLabel + ' d-block'}>{t('mockServiceDetails.created')} </label>
            <div className="mt-1 text-truncate">
              <span title={createdMessage}> {createdMessage}</span>
            </div>
          </Col>
          {/* ./Created */}

          {/* Updated */}
          <Col md={2}>
            <label className="d-block">{t('mockServiceDetails.updated')}</label>
            <div className="mt-1 text-truncate">
              <span title={updatedMessage}> {updatedMessage}</span>
            </div>
          </Col>
          {/* ./Updated */}

          {/* Action Buttons */}
          <Col md={2} className={'text-right pt-4'}>
            {!this.props.isSaving && (
              <div>
                {/* Discard Local Changes button */}
                {this.props.template.isDraft && this.props.template.serviceMockTemplateId && (
                  <Button variant={'link'} onClick={() => this.props.discardLocalTemplateChanges(this.props.template)}>
                    Discard Local Changes
                  </Button>
                )}
                {/* ./ Discard Local Changes button */}
                <Button
                  disabled={!this.props.template.isDraft || this.props.isReadOnly}
                  onClick={this.props.saveTemplateHandler}
                >
                  {t('common:actions.save')}
                </Button>
              </div>
            )}
            {this.props.isSaving && (
              <SimpleLoader loadingText={t('mockServices:mockServicesTable.statusMsg.pleaseWait')} />
            )}
          </Col>
          {/* ./Action Buttons */}
        </Row>

        <div className={styles.spacer} />

        <MockServiceDetailTabs
          mockService={this.props.template as MockService}
          disabled={false}
          activeTab={this.props.activeTab}
          changeActiveTabHandler={(tab: string) => {
            this.props.updateActiveTab(tab as any);
          }}
          updateParameters={(state: MockParameterState, debounce?: boolean) => {
            if (debounce) {
              this.onUpdateTemplatePropsDebounced(state);
            } else {
              this.onUpdateTemplateProps(state);
            }
          }}
          updateIterationOverrides={this.updateDataPublishConfigHandler}
        />
      </div>
    );
  }
}

function mapStateToProps({ blazeUserConfig, mockServiceTemplateDetail, configurations }: ApplicationState) {
  return {
    blazeUserConfig: blazeUserConfig.blazeUserConfig,
    activeTab: mockServiceTemplateDetail.activeTab,
    assetBundle: mockServiceTemplateDetail.assets.assetBundle,
    configurations: configurations.configurations,
    activeParamTab: mockServiceTemplateDetail.activeParamsTab,
  };
}

const mapDispatchToProps = {
  updateMockServiceTemplate,
  showToastNotification,
  updateActiveTab,
  discardLocalTemplateChanges,
  updateTemplateAssets,
  getAssetsForTemplate,
  updateActiveParamTab: updateTemplateActiveParamTab,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default withTranslation('mockServices')(connect(mapStateToProps, mapDispatchToProps)(MockTemplateDetail));
